import { useNeedsYouState } from '../../shell/ShellContext'
import { scopeQueue } from '../../shell/queueScope'
import { Alert, Button, CardList, DegradedState } from '../../ds'

/**
 * Ledger's Shadow banner and "waiting on you" banner (MAR-309), from the v6 prototype.
 *
 * Both read the SHELL's queue (`useNeedsYouState` → the one `useNeedsYou` AppShell runs), so the
 * banners, the bell and the Inbox drawer are one fetch and one number. Today ran its own fetches
 * instead, read held drafts whatever the mode, and so showed a live workspace "needs you" items
 * that the Inbox — correctly — did not: a draft Shadow held cannot be sent once the workspace is
 * live, so in live mode it is waiting on no one.
 *
 * ⚠️ Each category is named ONCE on the page. The design's live-mode note counts the escalations
 * twice (its `queue` falls back to the escalations when there are no held drafts), and putting held
 * drafts in both banners would be the same mistake: two banners and two buttons for one queue. So
 * the split is by category, not by mode:
 *  · the Shadow banner (Shadow only) owns the held drafts — their count, their "20+", and their own
 *    pending/stale states;
 *  · the waiting banner owns the NOTIFICATIONS — questions and escalations — and nothing else. With
 *    none of those it does not render, even while drafts are held.
 * The bell and the Inbox still total everything, so in Shadow the bell's figure is the two banners
 * added together; in live mode there are no drafts and the waiting banner's count IS the bell's.
 *
 * ⚠️ The honest states are the bell's (`AppShell.tsx`, `bell`), scoped to the categories each banner
 * describes, and a queue that has not been read, or could not be, never reads as an empty one:
 *  · `initialising` — nothing read yet: no banner at all;
 *  · degraded — a notification read failed: the count is "at the last check", plus "there may be
 *    more" when one has never loaded; with nothing known to be waiting it says the read failed,
 *    never "nothing waiting". A held-drafts failure is the Shadow banner's to say, not this one's;
 *  · incomplete (not degraded) — a notification read still in flight: "so far".
 * And the shadow read shows 20: more behind them is "20+", never "20".
 */

const s = (n: number, more = false) => (n === 1 && !more ? '' : 's')

/** What the waiting banner describes. Held drafts are the Shadow banner's. */
const NOTIFICATIONS = ['escalations', 'questions'] as const

export default function LedgerBanners({ isShadow }: { isShadow: boolean }) {
  const shell = useNeedsYouState()
  // Outside the shell there is no queue to describe — and no basis for an all-clear either.
  if (!shell) return null
  const { needsYou: q, openInbox } = shell
  if (q.initialising) return null

  // `shadowHasMore` is false in live mode by construction (masked with `shadow`), so this "+"
  // only ever rides on a Shadow count.
  const more = isShadow && q.shadowHasMore
  const held = isShadow ? q.shadow.length : 0
  const heldStr = more ? `${held}+` : String(held)

  // ── Shadow ──────────────────────────────────────────────────────────────────────
  let shadowBanner: React.ReactNode = null
  if (isShadow) {
    const shadowPending = q.pending.includes('shadow')
    const shadowStale = q.stale.includes('shadow')
    // Not read yet, or failed with nothing ever read: the count is unknown, so it is not given.
    const title =
      shadowPending || (shadowStale && held === 0)
        ? 'Shadow · nothing sent'
        : `Shadow · ${heldStr} draft${s(held, more)} held, nothing sent${shadowStale ? ' at the last check' : ''}`
    shadowBanner = (
      <Alert
        variant="banner"
        orb
        title={title}
        data-testid="ledger-banner-shadow"
        actions={
          <Button variant="gold" size="sm" onClick={openInbox}>
            Review the drafts
          </Button>
        }
      >
        While she is in Shadow, nothing she drafts reaches a customer — these wait for your call.
      </Alert>
    )
  }

  // ── Waiting on you ──────────────────────────────────────────────────────────────
  // Notifications only, in either mode. In live mode `q.shadow` is masked empty, so this equals the
  // bell's `total` exactly; in Shadow the drafts are already named above. No "+": the floor belongs
  // to the drafts, and the notification reads are not paged.
  // The narrowing is `scopeQueue`'s, shared with Knowledge's Inbox banner, so the two banners
  // cannot state their honest states two different ways.
  const qn = q.questions.length
  const en = q.escalations.length
  const { total, degraded, unknown, incomplete } = scopeQueue(q, NOTIFICATIONS)
  let waitingBanner: React.ReactNode = null
  if (total > 0) {
    const one = total === 1
    const title = degraded
      ? `${total} thing${one ? ' was' : 's were'} waiting on you at the last check${unknown ? ' — there may be more' : ''}`
      : incomplete
        ? `${total} thing${one ? ' is' : 's are'} waiting on you so far`
        : `${total} thing${one ? ' is' : 's are'} waiting on you`
    const parts: string[] = []
    if (qn > 0) parts.push(`${qn} question${s(qn)} she could not answer`)
    if (en > 0) parts.push(en === 1 ? '1 action over her bound' : `${en} actions over her bounds`)
    waitingBanner = (
      <Alert
        variant="banner"
        tone="attention"
        orb
        title={title}
        data-testid="ledger-banner-waiting"
        actions={
          <Button variant="gold" size="sm" onClick={openInbox}>
            Open the inbox
          </Button>
        }
      >
        {parts.length > 0 ? parts.join(' · ') : undefined}
      </Alert>
    )
  } else if (degraded) {
    // A failed read with nothing known to be waiting is NOT an all-clear.
    waitingBanner = (
      <DegradedState title="She couldn’t check what is waiting on you just now — the bell will show it when she can." />
    )
  }

  if (!shadowBanner && !waitingBanner) return null
  return (
    <CardList data-testid="ledger-banners">
      {shadowBanner}
      {waitingBanner}
    </CardList>
  )
}
